import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    useTheme,
    alpha
} from '@mui/material';
import {
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    format,
    addWeeks,
    subWeeks,
    isToday,
    startOfDay,
    isBefore,
    isSameDay,
    addDays
} from 'date-fns';
import { es } from 'date-fns/locale';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AndroidRoundedIcon from '@mui/icons-material/AndroidRounded';
import { useTareas } from '../../context/TareasContext';
import { useObras } from '../../context/ObrasContext';
import TareaCard from './TareaCard';
import AddTaskDialog from './AddTaskDialog';
import { Tarea } from '../../types';

const TareasKanban: React.FC = () => {
    const { tareas, agregarTarea, completarTarea, actualizarTarea, eliminarTarea } = useTareas();
    const { obras } = useObras();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState<Tarea | null>(null);
    const theme = useTheme();



    // Auto-move overdue tasks to today (Business Logic kept intact)
    useEffect(() => {
        const checkAndMoveOverdueTasks = async () => {
            const today = startOfDay(new Date());
            const overdueTasks = tareas.filter(t =>
                !t.recurrente &&
                !t.completada &&
                t.fechaProgramada &&
                isBefore(startOfDay(new Date(t.fechaProgramada)), today)
            );

            for (const task of overdueTasks) {
                await actualizarTarea(task.id, { fechaProgramada: new Date() });
            }
        };

        if (tareas.length > 0) {
            checkAndMoveOverdueTasks();
        }
    }, [tareas, actualizarTarea]);

    const daysOfWeek = useMemo(() => {
        const start = startOfDay(currentDate);
        const end = addDays(start, 6);
        return eachDayOfInterval({ start, end });
    }, [currentDate]);

    const handleDragEnd = async (result: DropResult) => {
        const { destination, source, draggableId } = result;

        if (!destination) return;

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        const sourceDay = new Date(source.droppableId);
        const destDay = new Date(destination.droppableId);

        if (source.droppableId === destination.droppableId) {
            // Reordering within the same day
            const dayTasks = getTasksForDay(sourceDay);
            const newTasks = Array.from(dayTasks);
            const [movedTask] = newTasks.splice(source.index, 1);
            newTasks.splice(destination.index, 0, movedTask);

            // Update orders for all affected tasks
            // We batch these updates to avoid UI flickering if possible, 
            // though Firestore updates are async.
            const updates = newTasks.map((task, index) => {
                if (task.order !== index) {
                    return actualizarTarea(task.id, { order: index });
                }
                return Promise.resolve();
            });

            await Promise.all(updates);

        } else {
            // Moving to a different day
            const newDate = new Date(destDay);
            newDate.setHours(12, 0, 0, 0);

            // Get tasks for destination day to find insertion point
            const destTasks = getTasksForDay(destDay);
            const newDestTasks = Array.from(destTasks);

            // We don't have the moved task object in destTasks yet, so we just calculate the orders
            // The moved task will be at destination.index
            // Tasks currently at i >= destination.index will be shifted to i + 1

            const updates = [];

            // Update the moved task
            updates.push(actualizarTarea(draggableId, {
                fechaProgramada: newDate,
                order: destination.index
            }));

            // Shift other tasks in destination
            newDestTasks.forEach((task, index) => {
                if (index >= destination.index) {
                    updates.push(actualizarTarea(task.id, { order: index + 1 }));
                } else {
                    // Normalize existing orders if needed (optional but good for consistency)
                    if (task.order !== index) {
                        updates.push(actualizarTarea(task.id, { order: index }));
                    }
                }
            });

            await Promise.all(updates);
        }
    };

    const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
    const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));

    const handleOpenAddDialog = () => {
        setEditingTask(null);
        setIsAddDialogOpen(true);
    };
    const handleCloseAddDialog = () => {
        setEditingTask(null);
        setIsAddDialogOpen(false);
    };

    const handleToggleTarea = async (tarea: Tarea, dayContext?: Date) => {
        if (tarea.recurrente && dayContext) {
            const dateStr = format(dayContext, 'yyyy-MM-dd');
            const currentCompletions = tarea.completionesRecurrentes || [];
            let newCompletions;

            if (tarea.completada) {
                newCompletions = currentCompletions.filter(d => d !== dateStr);
            } else {
                newCompletions = [...currentCompletions, dateStr];
            }

            await actualizarTarea(tarea.id, {
                completionesRecurrentes: newCompletions
            });
        } else {
            let updates: Partial<Tarea> = {
                completada: !tarea.completada,
                fechaCompletacion: !tarea.completada ? new Date() : null
            };
            await actualizarTarea(tarea.id, updates);
        }
    };

    const getTasksForDay = (day: Date) => {
        return tareas.filter(tarea => {
            if (!tarea.fechaProgramada) return false;

            const fechaTarea = new Date(tarea.fechaProgramada);
            const currentColumnDate = startOfDay(day);

            if (tarea.recurrente) {
                if (fechaTarea.getDay() === day.getDay() && day >= startOfWeek(fechaTarea, { weekStartsOn: 1 })) {
                    return true;
                }
                return false;
            }

            if (isSameDay(fechaTarea, day)) {
                return true;
            }

            return false;
        }).map(tarea => {
            if (tarea.recurrente) {
                const dateStr = format(day, 'yyyy-MM-dd');
                const isCompletedForToday = tarea.completionesRecurrentes?.includes(dateStr) || false;
                return {
                    ...tarea,
                    completada: isCompletedForToday
                };
            }
            return tarea;
        }).sort((a, b) => {
            // First sort by completion status (completed items go to bottom)
            if (a.completada !== b.completada) {
                return a.completada ? 1 : -1;
            }
            // Then sort by custom order
            const orderA = a.order ?? 999999;
            const orderB = b.order ?? 999999;
            return orderA - orderB;
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 2, sm: 4 }, // Responsive padding
                py: { xs: 2, sm: 3 },
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                zIndex: 20,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                flexWrap: 'wrap', // Allow wrapping on very small screens
                gap: 2
            }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Box
                        sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            display: 'flex'
                        }}
                    >
                        <CalendarTodayRoundedIcon fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight="800" sx={{ letterSpacing: '-0.5px', color: 'text.primary' }}>
                            {format(currentDate, 'MMMM yyyy', { locale: es }).replace(/^\w/, (c) => c.toUpperCase())}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary" fontWeight="500">
                                Semana {format(daysOfWeek[0], 'd')} - {format(daysOfWeek[6], 'd', { locale: es })}
                            </Typography>
                            {/* Navigation inside header context */}
                            <Box display="flex" gap={0.5} ml={1}>
                                <IconButton onClick={handlePrevWeek} size="small" sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                    <ChevronLeftRoundedIcon fontSize="small" />
                                </IconButton>
                                <IconButton onClick={handleNextWeek} size="small" sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                    <ChevronRightRoundedIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box display="flex" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={<AndroidRoundedIcon />}
                        href="https://materiales-byb-49a93.web.app/mat_byb_widget.apk"
                        target="_blank"
                        download="mat_byb_widget.apk"
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2,
                            py: 1,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 2,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                            }
                        }}
                    >
                        App para Widgets
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={handleOpenAddDialog}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            py: 1,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: `0 8px 16px -4px ${alpha(theme.palette.primary.main, 0.5)}`,
                            '&:hover': {
                                boxShadow: `0 12px 20px -8px ${alpha(theme.palette.primary.main, 0.8)}`,
                            }
                        }}
                    >
                        Nueva Tarea
                    </Button>
                </Box>
            </Box>

            {/* Kanban Board Container */}
            <DragDropContext onDragEnd={handleDragEnd}>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        p: { xs: 2, sm: 3 },
                        overflowX: { xs: 'hidden', sm: 'auto' },
                        overflowY: { xs: 'auto', sm: 'hidden' },
                        scrollSnapType: { xs: 'y mandatory', sm: 'x mandatory' },
                        alignItems: 'stretch',
                        height: '100%',
                        // Scrollbar styling
                        '&::-webkit-scrollbar': {
                            height: 8,
                            width: 8
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                            borderRadius: 4
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: alpha(theme.palette.text.secondary, 0.2),
                            borderRadius: 4,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.text.secondary, 0.3)
                            }
                        }
                    }}
                >
                    {daysOfWeek.map((day) => {
                        const dayTasks = getTasksForDay(day);
                        const isTodayColumn = isToday(day);
                        const dateStr = day.toISOString();

                        return (
                            <Box
                                key={day.toISOString()}
                                sx={{
                                    flexShrink: 0,
                                    width: { xs: '100%', sm: 340 },
                                    maxHeight: '100%', // Ensure column doesn't overflow container
                                    display: 'flex',
                                    flexDirection: 'column',
                                    scrollSnapAlign: 'start',
                                    position: 'relative',
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    bgcolor: isTodayColumn ? alpha(theme.palette.primary.main, 0.04) : '#ffffff',
                                    border: isTodayColumn ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1px solid',
                                    borderColor: isTodayColumn ? 'transparent' : theme.palette.divider,
                                    boxShadow: isTodayColumn ? `0 4px 20px -5px ${alpha(theme.palette.primary.main, 0.15)}` : '0 1px 3px rgba(0,0,0,0.02)',
                                    mb: { xs: 2, sm: 0 }
                                }}
                            >
                                {/* Column Header */}
                                <Box
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderBottom: isTodayColumn ? `1px solid ${alpha(theme.palette.primary.main, 0.1)}` : '1px solid transparent'
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <Box
                                            sx={{
                                                width: 42,
                                                height: 42,
                                                borderRadius: '12px',
                                                bgcolor: isTodayColumn ? 'primary.main' : 'white',
                                                color: isTodayColumn ? 'white' : 'text.primary',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: isTodayColumn ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}` : '0 1px 3px rgba(0,0,0,0.05)',
                                                border: isTodayColumn ? 'none' : '1px solid',
                                                borderColor: 'divider',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', lineHeight: 1, opacity: 0.8 }}>
                                                {format(day, 'EEE', { locale: es })}
                                            </Typography>
                                            <Typography variant="h6" fontWeight="800" lineHeight={1}>
                                                {format(day, 'd')}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="700" sx={{ lineHeight: 1.2, color: isTodayColumn ? 'primary.main' : 'text.primary' }}>
                                                {isTodayColumn ? 'Hoy' : format(day, 'EEEE', { locale: es }).replace(/^\w/, (c) => c.toUpperCase())}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                                {dayTasks.length} {dayTasks.length === 1 ? 'tarea' : 'tareas'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton size="small" sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                                        <MoreHorizIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                {/* Droppable Area */}
                                <Droppable droppableId={dateStr}>
                                    {(provided, snapshot) => (
                                        <Box
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            sx={{
                                                flex: 1,
                                                p: 1.5,
                                                overflowY: 'auto',
                                                minHeight: 150,
                                                transition: 'background-color 0.2s',
                                                bgcolor: snapshot.isDraggingOver ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                                                borderRadius: 3,
                                                m: 1,
                                                mt: 0,
                                                scrollbarWidth: 'none',
                                                '&::-webkit-scrollbar': { display: 'none' }
                                            }}
                                        >
                                            {dayTasks.map((tarea, index) => (
                                                <Draggable key={tarea.id} draggableId={tarea.id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <TareaCard
                                                            ref={provided.innerRef}
                                                            tarea={tarea}
                                                            onCompletar={() => handleToggleTarea(tarea, day)}
                                                            onUpdate={actualizarTarea}
                                                            onEdit={(task) => {
                                                                setEditingTask(task);
                                                                setIsAddDialogOpen(true);
                                                            }}
                                                            onDelete={eliminarTarea}
                                                            isDragging={snapshot.isDragging}
                                                            draggableProps={provided.draggableProps}
                                                            dragHandleProps={provided.dragHandleProps}
                                                        />
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}

                                            {/* Empty State */}
                                            {dayTasks.length === 0 && !snapshot.isDraggingOver && (
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: 0.3,
                                                        gap: 1,
                                                        py: 4
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '50%',
                                                            border: '2px dashed',
                                                            borderColor: 'text.secondary',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <AddRoundedIcon fontSize="small" />
                                                    </Box>
                                                    <Typography variant="caption" fontWeight="600">Sin tareas</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Droppable>
                            </Box>
                        );
                    })}
                </Box>
            </DragDropContext >

            <AddTaskDialog
                open={isAddDialogOpen}
                onClose={handleCloseAddDialog}
                onAdd={agregarTarea}
                daysOfWeek={daysOfWeek}
                obras={obras}
                taskToEdit={editingTask}
                onUpdate={actualizarTarea}
            />
        </Box >
    );
};

export default TareasKanban;
